import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useClients, useCreateClient, useCreateInvoice, useKboLookup, useQuotaStatus } from '@/hooks/useApi';
import DescriptionAutocomplete from '@/components/DescriptionAutocomplete';
import QuotaExceededBanner from '@/components/QuotaExceededBanner';
import { getApiErrorCode } from '@/lib/errorCodes';
import type { KboEnterprise } from '@/types';
import toast from 'react-hot-toast';

const DRAFT_STORAGE_KEY = 'invoice_create_draft';

interface DraftState {
  clientId: string;
  issueDate: string;
  dueDate: string;
  lines: LineItem[];
  notes: string;
  savedAt: number;
}

function loadDraft(): DraftState | null {
  try {
    const raw = localStorage.getItem(DRAFT_STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw) as DraftState;
  } catch {
    return null;
  }
}

function saveDraft(state: DraftState) {
  try {
    localStorage.setItem(DRAFT_STORAGE_KEY, JSON.stringify({ ...state, savedAt: Date.now() }));
  } catch {
    // Ignore storage quota issues.
  }
}

function clearDraft() {
  localStorage.removeItem(DRAFT_STORAGE_KEY);
}

interface LineItem {
  description: string;
  quantity: number;
  unitPrice: string;
  vatRate: string;
}

const VAT_RATES = ['21', '12', '6', '0'];

export default function InvoiceCreatePage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { data: clientsData } = useClients({});
  const createInvoice = useCreateInvoice();
  const createClient = useCreateClient();
  const quota = useQuotaStatus();
  const isQuotaBlocked = quota.isLoaded && !quota.canCreateInvoice;
  const canUseDraftNotifications = quota.isLoaded && quota.canCreateInvoice;
  const draft = useMemo(() => loadDraft(), []);

  const [issueDate, setIssueDate] = useState(draft?.issueDate ?? new Date().toISOString().slice(0, 10));
  const [dueDate, setDueDate] = useState(
    draft?.dueDate ?? new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10)
  );
  const [lines, setLines] = useState<LineItem[]>(
    draft?.lines ?? [{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]
  );
  const [notes, setNotes] = useState(draft?.notes ?? '');
  const [showClientModal, setShowClientModal] = useState(false);
  const emptyClientForm = {
    name: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    postalCode: '',
    country: 'BE',
    vatNumber: '',
    type: 'company',
  };
  const [clientForm, setClientForm] = useState(emptyClientForm);
  const [vatLookup, setVatLookup] = useState('');
  const { data: kboData, isFetching: kboLoading } = useKboLookup(vatLookup);
  const [hasStoredDraft, setHasStoredDraft] = useState(Boolean(draft));
  const draftToastShown = useRef(false);
  const hasDraftContentRef = useRef(false);
  const skipLeaveToastRef = useRef(false);
  const effectInitializedRef = useRef(false);

  // Initialize clientId from draft or URL parameter
  const [clientId, setClientId] = useState(() => draft?.clientId || '');

  // Sync URL query parameter to clientId when component mounts or URL changes
  useEffect(() => {
    const urlClientId = searchParams.get('clientId');
    if (urlClientId && !draft?.clientId) {
      setClientId(urlClientId);
    }
  }, [searchParams, draft?.clientId]);

  const hasDraftContent = Boolean(
    clientId
    || notes.trim()
    || lines.some((line) => line.description.trim() || line.unitPrice !== '' || line.quantity !== 1 || line.vatRate !== '21')
  );

  hasDraftContentRef.current = hasDraftContent;

  useEffect(() => {
    if (!canUseDraftNotifications) {
      return;
    }

    const timer = setTimeout(() => {
      if (!hasDraftContent) {
        clearDraft();
        setHasStoredDraft(false);
        return;
      }

      saveDraft({
        clientId,
        issueDate,
        dueDate,
        lines,
        notes,
        savedAt: Date.now(),
      });
      setHasStoredDraft(true);
    }, 500);

    return () => clearTimeout(timer);
  }, [canUseDraftNotifications, clientId, dueDate, hasDraftContent, issueDate, lines, notes]);

  useEffect(() => {
    if (!canUseDraftNotifications) {
      return;
    }

    if (draft && !draftToastShown.current) {
      draftToastShown.current = true;
      toast.success(t('invoices.draftRestored'), { id: 'invoice-draft-restored' });
    }
  }, [canUseDraftNotifications, draft, t]);

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (!canUseDraftNotifications || !hasDraftContentRef.current || skipLeaveToastRef.current) {
        return;
      }

      event.preventDefault();
      event.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [canUseDraftNotifications]);

  useEffect(() => {
    if (!effectInitializedRef.current) {
      effectInitializedRef.current = true;
      return;
    }

    return () => {
      if (canUseDraftNotifications && hasDraftContentRef.current && !skipLeaveToastRef.current) {
        toast.success(t('invoices.draftSaved'), { id: 'invoice-draft-saved' });
      }
    };
  }, [canUseDraftNotifications, t]);

  function addLine() {
    setLines([...lines, { description: '', quantity: 1, unitPrice: '', vatRate: '21' }]);
  }

  function removeLine(index: number) {
    if (lines.length <= 1) return;
    setLines(lines.filter((_, i) => i !== index));
  }

  function updateLine(index: number, field: keyof LineItem, value: string | number) {
    setLines(lines.map((l, i) => (i === index ? { ...l, [field]: value } : l)));
  }

  function lineTotal(line: LineItem) {
    const net = line.quantity * Number(line.unitPrice || 0);
    const vat = net * (Number(line.vatRate) / 100);
    return { net, vat, gross: net + vat };
  }

  const totals = lines.reduce(
    (acc, l) => {
      const lt = lineTotal(l);
      acc.net += lt.net;
      acc.vat += lt.vat;
      acc.gross += lt.gross;
      return acc;
    },
    { net: 0, vat: 0, gross: 0 }
  );

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!clientId) {
      toast.error(t('invoices.selectClient'));
      return;
    }
    createInvoice.mutate(
      {
        clientId,
        issueDate,
        dueDate,
        notes,
        lines: lines.map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unitPrice: l.unitPrice,
          vatRate: l.vatRate,
        })),
      },
      {
        onSuccess: (data) => {
          skipLeaveToastRef.current = true;
          clearDraft();
          toast.success(t('invoices.created'));
          navigate(`/invoices/${data.id}`);
        },
        onError: (error) => {
          const errorCode = getApiErrorCode(error);
          if (errorCode) {
            const localized = t(errorCode);
            if (localized !== errorCode) {
              toast.error(localized);
              return;
            }
          }
          toast.error(t('common.error'));
        },
      }
    );
  }

  function handleBack() {
    navigate(-1);
  }

  function handleResetAll() {
    if (!window.confirm(t('invoices.resetConfirm'))) return;

    setClientId('');
    setIssueDate(new Date().toISOString().slice(0, 10));
    setDueDate(new Date(Date.now() + 30 * 86400000).toISOString().slice(0, 10));
    setLines([{ description: '', quantity: 1, unitPrice: '', vatRate: '21' }]);
    setNotes('');
    setHasStoredDraft(false);
    clearDraft();
  }

  function handleQuickCreateClient(e: React.FormEvent) {
    e.preventDefault();
    createClient.mutate(clientForm, {
      onSuccess: (newClient) => {
        setClientId(newClient.id);
        setShowClientModal(false);
        setClientForm(emptyClientForm);
        setVatLookup('');
        toast.success(t('clients.created'));
      },
      onError: (error) => {
        const errorCode = getApiErrorCode(error);
        if (errorCode) {
          const localized = t(errorCode);
          if (localized !== errorCode) {
            toast.error(localized);
            return;
          }
        }
        toast.error(t('common.error'));
      },
    });
  }

  function applyKboData(kbo: KboEnterprise) {
    const street = [kbo.streetFr || kbo.streetNl, kbo.houseNumber, kbo.box].filter(Boolean).join(' ');
    setClientForm({
      ...clientForm,
      name: kbo.nameFr || kbo.nameNl || kbo.commercialName || clientForm.name,
      vatNumber: kbo.vatNumber,
      email: kbo.email || clientForm.email,
      phone: kbo.phone || clientForm.phone,
      address: street || clientForm.address,
      city: kbo.municipalityFr || kbo.municipalityNl || clientForm.city,
      postalCode: kbo.zipcode || clientForm.postalCode,
      country: 'BE',
      type: kbo.typeOfEnterprise === '1' ? 'individual' : 'company',
    });
    toast.success(t('kbo.dataApplied'));
  }

  if (isQuotaBlocked) {
    return (
      <div>
        <div className="flex items-center gap-3 mb-6">
          <button onClick={handleBack} className="text-gray-400 hover:text-gray-600">←</button>
          <h1 className="text-2xl font-bold">{t('invoices.createInvoice')}</h1>
        </div>
        <QuotaExceededBanner
          type="invoices"
          used={quota.invoicesUsed}
          limit={quota.invoicesLimit}
          planLabel={quota.planLabel}
          fullPage
        />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center gap-3 mb-6">
        <button onClick={handleBack} className="text-gray-400 hover:text-gray-600">←</button>
        <h1 className="text-2xl font-bold">{t('invoices.createInvoice')}</h1>
        {hasStoredDraft && (
          <div className="ml-auto">
            <button type="button" className="btn btn-secondary btn-sm text-red-600 border-red-200 hover:bg-red-50" onClick={handleResetAll}>
              {t('invoices.resetAll')}
            </button>
          </div>
        )}
      </div>

      <form onSubmit={handleSubmit}>
        <div className="max-w-6xl">
          <div className="space-y-6">
            {/* Client & dates */}
            <div className="card card-body grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="label">{t('invoices.client')} *</label>
                <div className="flex gap-2">
                  <select className="input flex-1" value={clientId} onChange={(e) => setClientId(e.target.value)} required>
                    <option value="">{t('invoices.selectClient')}</option>
                    {clientsData?.data?.map((c) => (
                      <option key={c.id} value={c.id}>{c.name}</option>
                    ))}
                  </select>
                  <button
                    type="button"
                    className="btn btn-secondary px-3"
                    title={t('clients.addClient')}
                    onClick={() => setShowClientModal(true)}
                  >
                    +
                  </button>
                </div>
              </div>
              <div>
                <label className="label">{t('invoices.date')} *</label>
                <input type="date" className="input" value={issueDate} onChange={(e) => setIssueDate(e.target.value)} required />
              </div>
              <div>
                <label className="label">{t('invoices.dueDate')} *</label>
                <input type="date" className="input" value={dueDate} onChange={(e) => setDueDate(e.target.value)} required />
              </div>
            </div>

            {/* Lines */}
            <div className="card rounded-xl shadow-sm">
              <div className="card-header flex items-center justify-between px-5 sm:px-6 py-3.5 sm:py-4 border-b border-gray-100">
                <h3 className="font-semibold text-base text-gray-900">{t('invoices.lines')}</h3>
                <button type="button" className="btn btn-secondary btn-sm" onClick={addLine}>
                  + {t('common.add')}
                </button>
              </div>
              <div className="overflow-x-auto">
                <table className="table">
                  <thead>
                    <tr>
                      <th>{t('invoices.description')}</th>
                      <th className="w-20">{t('invoices.quantity')}</th>
                      <th className="w-36">{t('invoices.unitPrice')}</th>
                      <th className="w-28">{t('invoices.vat')}</th>
                      <th className="text-right w-28">{t('common.amount')}</th>
                      <th className="w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {lines.map((line, i) => {
                      const lt = lineTotal(line);
                      return (
                        <tr key={i}>
                          <td>
                            <DescriptionAutocomplete
                              value={line.description}
                              onChange={(val) => updateLine(i, 'description', val)}
                              placeholder={t('invoices.description')}
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min={1}
                              className="input text-center"
                              value={line.quantity}
                              onChange={(e) => updateLine(i, 'quantity', Number(e.target.value))}
                              required
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              step="0.01"
                              className="input text-right"
                              value={line.unitPrice}
                              onChange={(e) => updateLine(i, 'unitPrice', e.target.value)}
                              placeholder="0.00"
                              required
                            />
                          </td>
                          <td>
                            <select className="input min-w-[5rem]" value={line.vatRate} onChange={(e) => updateLine(i, 'vatRate', e.target.value)}>
                              {VAT_RATES.map((r) => (
                                <option key={r} value={r}>{r}%</option>
                              ))}
                            </select>
                          </td>
                          <td className="text-right font-medium">€{lt.gross.toFixed(2)}</td>
                          <td>
                            <button
                              type="button"
                              className="text-red-400 hover:text-red-600"
                              onClick={() => removeLine(i)}
                              disabled={lines.length <= 1}
                            >
                              ✕
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="px-4 sm:px-6 py-3 border-t border-gray-100">
                  <button type="button" className="btn btn-secondary btn-sm w-full" onClick={addLine}>
                    + {t('common.add')}
                  </button>
                </div>
              </div>

              {/* Totals Summary */}
              <div className="px-4 sm:px-6 py-4 bg-gray-50 border-t border-gray-200 space-y-3">
                <div className="flex justify-end gap-8 text-sm">
                  <span className="text-gray-500 font-medium">{t('invoices.subtotal')}</span>
                  <span className="tabular-nums text-gray-900 text-right min-w-[100px]">€{totals.net.toFixed(2)}</span>
                </div>
                <div className="flex justify-end gap-8 text-sm">
                  <span className="text-gray-500 font-medium">{t('invoices.vat')}</span>
                  <span className="tabular-nums text-gray-900 text-right min-w-[100px]">€{totals.vat.toFixed(2)}</span>
                </div>
                <hr className="border-gray-300" />
                <div className="flex justify-end gap-8 text-base font-bold">
                  <span className="text-gray-900">{t('invoices.total')}</span>
                  <span className="tabular-nums text-gray-900 text-right min-w-[100px]">€{totals.gross.toFixed(2)}</span>
                </div>
              </div>
            </div>

            {/* Notes */}
            <div className="card card-body">
              <label className="label">{t('common.notes')}</label>
              <textarea
                className="input min-h-[80px]"
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder={t('invoices.notesPlaceholder')}
              />
            </div>

            {/* Submit Button */}
            <div className="flex justify-end pt-2">
              <button
                type="submit"
                className="btn btn-primary px-8 py-2.5 font-medium shadow-md hover:shadow-lg transition-all"
                disabled={createInvoice.isPending}
              >
                {createInvoice.isPending ? t('common.saving') : t('invoices.createInvoice')}
              </button>
            </div>
          </div>
        </div>
      </form>

      {showClientModal && (
        <div className="modal-backdrop">
          <form onSubmit={handleQuickCreateClient} className="card w-full max-w-lg modal-sheet">
            <div className="card-header"><h2 className="text-lg font-bold">{t('clients.addClient')}</h2></div>
            <div className="card-body space-y-4">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden">
                <button
                  type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors ${clientForm.type === 'individual' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setClientForm({ ...clientForm, type: 'individual', vatNumber: '' })}
                >
                  {t('clients.types.individual')}
                </button>
                <button
                  type="button"
                  className={`flex-1 py-2.5 text-sm font-medium transition-colors border-l border-gray-200 ${clientForm.type === 'company' ? 'bg-primary-600 text-white' : 'bg-white text-gray-600 hover:bg-gray-50'}`}
                  onClick={() => setClientForm({ ...clientForm, type: 'company' })}
                >
                  {t('clients.types.company')}
                </button>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="label">{t('clients.name')} *</label>
                  <input
                    type="text"
                    className="input"
                    value={clientForm.name}
                    onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })}
                    required
                    autoFocus
                  />
                </div>
                <div>
                  <label className="label">{t('clients.email')}</label>
                  <input
                    type="email"
                    className="input"
                    value={clientForm.email}
                    onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">{t('clients.phone')}</label>
                  <input
                    type="text"
                    className="input"
                    value={clientForm.phone}
                    onChange={(e) => setClientForm({ ...clientForm, phone: e.target.value })}
                  />
                </div>
                {clientForm.type === 'company' && (
                  <div className="col-span-2">
                    <label className="label">{t('clients.vatNumber')}</label>
                    <div className="flex gap-2">
                      <input
                        type="text"
                        className="input flex-1"
                        value={clientForm.vatNumber}
                        onChange={(e) => setClientForm({ ...clientForm, vatNumber: e.target.value })}
                        placeholder="BE0123456789"
                      />
                      <button
                        type="button"
                        className="btn btn-secondary text-xs whitespace-nowrap"
                        disabled={!clientForm.vatNumber || clientForm.vatNumber.length < 10 || kboLoading}
                        onClick={() => setVatLookup(clientForm.vatNumber)}
                      >
                        {kboLoading ? '...' : t('kbo.findCompany')}
                      </button>
                    </div>
                    {kboData && (
                      <div className="mt-2 p-3 bg-green-50 border border-green-200 rounded-lg text-sm">
                        <div className="font-medium text-green-800 mb-1">{kboData.nameFr || kboData.nameNl}</div>
                        <div className="text-green-700 text-xs space-y-0.5 mb-2">
                          {kboData.streetFr && <div>{kboData.streetFr} {kboData.houseNumber}, {kboData.zipcode} {kboData.municipalityFr}</div>}
                          {kboData.activities?.[0] && <div>{t('kbo.activity')}: {kboData.activities[0].descriptionFr || kboData.activities[0].naceCode}</div>}
                        </div>
                        <button type="button" className="btn btn-primary w-full text-sm py-1.5" onClick={() => applyKboData(kboData)}>
                          {t('kbo.useCompany')}
                        </button>
                      </div>
                    )}
                    {vatLookup && !kboLoading && !kboData && (
                      <p className="text-xs text-gray-400 mt-1">{t('kbo.notFound')}</p>
                    )}
                  </div>
                )}
                <div className="col-span-2">
                  <label className="label">{t('clients.address')}</label>
                  <input
                    type="text"
                    className="input"
                    value={clientForm.address}
                    onChange={(e) => setClientForm({ ...clientForm, address: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">{t('clients.city')}</label>
                  <input
                    type="text"
                    className="input"
                    value={clientForm.city}
                    onChange={(e) => setClientForm({ ...clientForm, city: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">{t('clients.postalCode')}</label>
                  <input
                    type="text"
                    className="input"
                    value={clientForm.postalCode}
                    onChange={(e) => setClientForm({ ...clientForm, postalCode: e.target.value })}
                  />
                </div>
                <div>
                  <label className="label">{t('clients.country')}</label>
                  <input
                    type="text"
                    className="input"
                    maxLength={2}
                    value={clientForm.country}
                    onChange={(e) => setClientForm({ ...clientForm, country: e.target.value.toUpperCase() })}
                  />
                </div>
              </div>
            </div>
            <div className="card-body border-t flex gap-2 justify-end">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => {
                  setShowClientModal(false);
                  setClientForm(emptyClientForm);
                  setVatLookup('');
                }}
              >
                {t('common.cancel')}
              </button>
              <button type="submit" className="btn btn-primary" disabled={createClient.isPending}>
                {createClient.isPending ? t('common.saving') : t('common.save')}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
